import { Col, Row } from 'react-bootstrap';
import React from 'react';
import { Form, Formik, FormikHelpers, FormikProps } from 'formik';
import * as Yup from 'yup';
import { useDispatch } from 'react-redux';
import FormField from '../../form/FormField';
import { processError, saveRiskRule } from './riskRulesActions';
import { ApiRiskRule } from '../../api';
import { FormikState } from 'formik/dist/types';
import Has from '../../auth/Has';
import { CAN_ADD_RISK_RULE, CAN_MODIFY_RISK_RULE } from '../../auth/permissions';
import { Button, SalvModal } from '@salvapp/ui-components';

const checkDecimalPlaces = (val: number | undefined | null, precision: number) =>
  val === undefined ||
  val === null ||
  Math.floor(val) === val ||
  (val.toString().split('.')[1].length || 0) <= precision;

const validationSchema = Yup.object().shape(
  {
    id: Yup.number(),
    reason: Yup.string().required('Required'),
    details: Yup.string().required('Required'),
    content: Yup.string().required('Required'),
    weight: Yup.number()
      .min(0, 'Too small')
      .max(9999999999.999, 'Too high')
      .test('at most 3 decimal places', 'Too many decimal places', (value) => checkDecimalPlaces(value, 3))
      .required('Required'),
    enabled: Yup.string().required('Required'),
  },
  []
);

export interface RiskRulesModalFormValues {
  reason: string;
  details: string;
  content: string;
  weight: number;
  enabled: boolean;
}

interface RiskRulesModalProps {
  show: boolean;
  onComplete: (riskRule?: ApiRiskRule) => void;
}

export const RiskRulesModal = ({ show, onComplete }: RiskRulesModalProps) => {
  const dispatch = useDispatch();
  const initialValues = {
    reason: '',
    weight: 1.0,
    details: '',
    enabled: false,
    content: '',
  };

  const initialFormikState: Partial<FormikState<RiskRulesModalFormValues>> = {
    values: initialValues,
    isSubmitting: false,
    isValidating: false,
  };

  const submit = async (
    values: RiskRulesModalFormValues,
    { setSubmitting, resetForm }: FormikHelpers<RiskRulesModalFormValues>
  ) => {
    try {
      await dispatch(saveRiskRule(values));
      resetForm(initialFormikState);
      onComplete();
    } catch (e) {
      await dispatch(processError(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Formik
      enableReinitialize={true}
      initialValues={initialValues}
      onSubmit={submit}
      validationSchema={validationSchema}
    >
      {({ isSubmitting, resetForm }: FormikProps<RiskRulesModalFormValues>) => {
        const handleClose = () => {
          onComplete();
          resetForm();
        };
        return (
          <SalvModal show={show} onHide={handleClose}>
            <Form>
              <SalvModal.Header>
                <Has anyPermissions={[CAN_ADD_RISK_RULE, CAN_MODIFY_RISK_RULE]}>
                  {(hasPermission: boolean) => {
                    if (hasPermission) {
                      return 'Add rule';
                    } else {
                      return 'View rule';
                    }
                  }}
                </Has>
              </SalvModal.Header>
              <SalvModal.Body>
                <Row>
                  <Col md={10}>
                    <FormField name='reason' label='Reason' />
                  </Col>
                  <Col md={2}>
                    <FormField name='weight' type='number' label='Weight' />
                  </Col>
                </Row>
                <FormField name='details' type='textarea' label='Details' />
                <Row>
                  <Col md={12}>
                    <FormField name='content' type='textarea' label='Content' />
                  </Col>
                </Row>
                <Row>
                  <Col md={12} className='d-flex justify-content-end'>
                    <FormField id='enabledCheck' name='enabled' type='checkbox' label='Active' />
                  </Col>
                </Row>
              </SalvModal.Body>

              <SalvModal.Footer>
                <Button variant='secondary' onClick={handleClose}>
                  Close
                </Button>
                <Has anyPermissions={[CAN_ADD_RISK_RULE, CAN_MODIFY_RISK_RULE]}>
                  <Button disabled={isSubmitting} type='submit' variant='primary'>
                    Save
                  </Button>
                </Has>
              </SalvModal.Footer>
            </Form>
          </SalvModal>
        );
      }}
    </Formik>
  );
};

export default RiskRulesModal;
